import apiClient from '@/shared/api/client'
import type {
  Employee, LeaveType, LeaveBalance, LeaveApplication,
  ShiftRoster, Attendance, AttendanceSummary,
  PayrollPeriod, PaySlip,
} from '@/shared/types/hr'

interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  size: number
  pages: number
}

// ── Employees ────────────────────────────────────────────────────────────────

export const employeesApi = {
  list: async (params?: Record<string, unknown>): Promise<PaginatedResponse<Employee>> => {
    const res = await apiClient.get<PaginatedResponse<Employee>>('/hr/employees', { params })
    return res.data
  },
  get: async (id: string): Promise<Employee> => {
    const res = await apiClient.get<Employee>(`/hr/employees/${id}`)
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<Employee> => {
    const res = await apiClient.post<Employee>('/hr/employees', data)
    return res.data
  },
  update: async (id: string, data: Record<string, unknown>): Promise<Employee> => {
    const res = await apiClient.patch<Employee>(`/hr/employees/${id}`, data)
    return res.data
  },
}

// ── Leave Types ──────────────────────────────────────────────────────────────

export const leaveTypesApi = {
  list: async (): Promise<LeaveType[]> => {
    const res = await apiClient.get<LeaveType[]>('/hr/leave-types')
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<LeaveType> => {
    const res = await apiClient.post<LeaveType>('/hr/leave-types', data)
    return res.data
  },
  update: async (id: string, data: Record<string, unknown>): Promise<LeaveType> => {
    const res = await apiClient.patch<LeaveType>(`/hr/leave-types/${id}`, data)
    return res.data
  },
}

// ── Leave Balances ───────────────────────────────────────────────────────────

export const leaveBalancesApi = {
  list: async (params?: Record<string, unknown>): Promise<LeaveBalance[]> => {
    const res = await apiClient.get<LeaveBalance[]>('/hr/leave-balances', { params })
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<LeaveBalance> => {
    const res = await apiClient.post<LeaveBalance>('/hr/leave-balances', data)
    return res.data
  },
}

// ── Leave Applications ───────────────────────────────────────────────────────

export const leaveApplicationsApi = {
  list: async (params?: Record<string, unknown>): Promise<PaginatedResponse<LeaveApplication>> => {
    const res = await apiClient.get<PaginatedResponse<LeaveApplication>>('/hr/leave-applications', { params })
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<LeaveApplication> => {
    const res = await apiClient.post<LeaveApplication>('/hr/leave-applications', data)
    return res.data
  },
  approve: async (id: string, data: { status: string; remarks?: string }): Promise<LeaveApplication> => {
    const res = await apiClient.patch<LeaveApplication>(`/hr/leave-applications/${id}/approve`, data)
    return res.data
  },
}

// ── Shift Roster ─────────────────────────────────────────────────────────────

export const shiftRostersApi = {
  list: async (params?: Record<string, unknown>): Promise<PaginatedResponse<ShiftRoster>> => {
    const res = await apiClient.get<PaginatedResponse<ShiftRoster>>('/hr/rosters', { params })
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<ShiftRoster> => {
    const res = await apiClient.post<ShiftRoster>('/hr/rosters', data)
    return res.data
  },
  bulkCreate: async (data: Record<string, unknown>): Promise<{ message: string }> => {
    const res = await apiClient.post<{ message: string }>('/hr/rosters/bulk', data)
    return res.data
  },
  update: async (id: string, data: Record<string, unknown>): Promise<ShiftRoster> => {
    const res = await apiClient.patch<ShiftRoster>(`/hr/rosters/${id}`, data)
    return res.data
  },
}

// ── Attendance ───────────────────────────────────────────────────────────────

export const attendanceApi = {
  list: async (params?: Record<string, unknown>): Promise<PaginatedResponse<Attendance>> => {
    const res = await apiClient.get<PaginatedResponse<Attendance>>('/hr/attendance', { params })
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<Attendance> => {
    const res = await apiClient.post<Attendance>('/hr/attendance', data)
    return res.data
  },
  update: async (id: string, data: Record<string, unknown>): Promise<Attendance> => {
    const res = await apiClient.patch<Attendance>(`/hr/attendance/${id}`, data)
    return res.data
  },
  summary: async (params: { attendance_date: string; department_id: string; shift_id: string }): Promise<AttendanceSummary> => {
    const res = await apiClient.get<AttendanceSummary>('/hr/attendance/summary', { params })
    return res.data
  },
}

// ── Payroll ──────────────────────────────────────────────────────────────────

export const payrollPeriodsApi = {
  list: async (params?: Record<string, unknown>): Promise<PayrollPeriod[]> => {
    const res = await apiClient.get<PayrollPeriod[]>('/hr/payroll-periods', { params })
    return res.data
  },
  create: async (data: Record<string, unknown>): Promise<PayrollPeriod> => {
    const res = await apiClient.post<PayrollPeriod>('/hr/payroll-periods', data)
    return res.data
  },
  compute: async (id: string): Promise<PayrollPeriod> => {
    const res = await apiClient.post<PayrollPeriod>(`/hr/payroll-periods/${id}/compute`)
    return res.data
  },
  approve: async (id: string): Promise<PayrollPeriod> => {
    const res = await apiClient.post<PayrollPeriod>(`/hr/payroll-periods/${id}/approve`)
    return res.data
  },
}

export const payslipsApi = {
  list: async (periodId: string, params?: Record<string, unknown>): Promise<PaySlip[]> => {
    const res = await apiClient.get<PaySlip[]>(`/hr/payroll-periods/${periodId}/payslips`, { params })
    return res.data
  },
  get: async (id: string): Promise<PaySlip> => {
    const res = await apiClient.get<PaySlip>(`/hr/payslips/${id}`)
    return res.data
  },
}
